import { useMemo, useState } from "react";
import { connect } from "react-redux";

import SelectSearch from "@components/Form/SelectSearch";
import PhoneBuyBackSimulationQuestions from "@components/PhoneBuyBack/PhoneBuyBackSimulationQuestions";
import { addAlert, removeModal } from "@store/app/actions";
import Button from "@components/Button";
import Select from "@components/Form/Select";

const PhoneBuyBackSimulation = ({ devices, flowChart, removeModal }) => {
  const firstQuestion = flowChart[0];

  const [selection, setSelection] = useState({
    make: null,
    model: null,
    memory: "", // memory can be empty string as some devices don't have memory options
  });
  const [deviceGrade, setDeviceGrade] = useState();
  const [questions, setQuestions] = useState([firstQuestion]);

  const phoneBrands = useMemo(() => Object.keys(devices).sort(), [devices]);

  const phoneModelsInBrand = useMemo(
    () => selection.make && Object.keys(devices[selection.make]).sort(),
    [selection.make, devices]
  );

  const hasMemoryOptions = useMemo(
    () =>
      selection.make &&
      selection.model &&
      Object.keys(devices[selection.make][selection.model])?.[0].length > 0,
    [selection.make, selection.model, devices]
  );

  const phoneMemoryOptions = useMemo(
    () =>
      hasMemoryOptions && Object.keys(devices[selection.make][selection.model]),
    [selection.model, selection.make, devices, hasMemoryOptions]
  );

  const showQuestions = hasMemoryOptions
    ? selection.make && selection.model && selection.memory
    : selection.make && selection.model;

  const showDeviceValueInfo =
    (deviceGrade && deviceGrade === "D") ||
    questions[flowChart.length - 1]?.answer;

  const handleQuestionAnswer = ({ question }) => {
    if (!question) {
      return;
    }

    const { grade, question_id } = question;

    const currentQuestionIndex = questions.findIndex(
      (q) => q.question_id === question_id
    );

    if (currentQuestionIndex === -1) {
      return;
    }

    const newQuestions = [...questions];

    newQuestions[currentQuestionIndex] = question;

    if (grade === "D") {
      setDeviceGrade(grade);

      if (currentQuestionIndex < newQuestions.length - 1) {
        setQuestions(newQuestions.slice(0, currentQuestionIndex + 1));
      } else {
        setQuestions(newQuestions);
      }
    } else {
      const nextQuestionIndex = currentQuestionIndex + 1;

      if (nextQuestionIndex <= flowChart.length - 1) {
        if (questions[nextQuestionIndex]) {
          newQuestions[nextQuestionIndex] = flowChart[nextQuestionIndex];
          setQuestions(newQuestions.slice(0, nextQuestionIndex + 1));
          setDeviceGrade(grade);
        } else {
          const nextQuestion = flowChart[nextQuestionIndex];

          setQuestions([...newQuestions, nextQuestion]);
          setDeviceGrade(grade);
        }
      } else {
        setQuestions(newQuestions);
        setDeviceGrade(grade);
      }
    }
  };

  const updateSelection = (updates) => {
    setSelection((prev) => ({ ...prev, ...updates }));

    if (deviceGrade) {
      setDeviceGrade(null);
    }

    if (questions && questions[0].answer) {
      setQuestions([firstQuestion]);
    }
  };

  const handleMakeChange = ({ value }) => {
    updateSelection({ make: value, model: null, memory: "" });
  };

  const handleModelChange = ({ value }) => {
    updateSelection({ model: value, memory: "" });
  };

  const handleMemoryChange = ({ value }) => {
    updateSelection({ memory: value });
  };

  return (
    <div className="md:max-w-[75%] md:m-auto overflow-x-hidden">
      <div className="text-[24px] pb-[15px]">
        Descopera valoarea estimata a dispozitivului tau
      </div>

      <div className="text-[14px] pb-[25px]">
        Cu Altex Buy Back Program, este usor sa-ti schimbi dispozitivul eligibil
        in schimbul unui credit sau sa il reciclezi gratuit.
      </div>

      <div className="space-y-[15px] pt-[40px]">
        <SelectSearch
          input={{
            onChange: handleMakeChange,
            value: selection.make
              ? { value: selection.make, label: selection.make }
              : "",
          }}
          placeholder="Selecteaza marca"
          maxMenuHeight={150}
          options={phoneBrands.map((brand) => ({
            value: brand,
            label: brand,
          }))}
        />

        {selection.make && (
          <SelectSearch
            input={{
              onChange: handleModelChange,
              value: selection.model
                ? { value: selection.model, label: selection.model }
                : "",
            }}
            placeholder="Selecteaza modelul"
            maxMenuHeight={150}
            options={
              phoneModelsInBrand &&
              phoneModelsInBrand.map((model) => ({
                value: model,
                label: model,
              }))
            }
          />
        )}

        {selection.make && selection.model && hasMemoryOptions && (
          <Select
            input={{
              onChange: (event) =>
                handleMemoryChange({ value: event.target.value }),
              value: selection.memory ? selection.memory : "",
            }}
            meta={{}}
            placeholder="Selecteaza memoria"
          >
            <option value="">Selecteaza memoria</option>
            {phoneMemoryOptions.map((memory) => (
              <option key={memory} value={memory}>
                {memory}
              </option>
            ))}
          </Select>
        )}
      </div>

      {showQuestions && (
        <>
          <ul className="py-[30px] space-y-[25px] border-t border-[#D9D9D9] mt-[40px]">
            <PhoneBuyBackSimulationQuestions
              questions={questions}
              onQuestionAnswer={handleQuestionAnswer}
            />
          </ul>

          {showDeviceValueInfo && (
            <div className="flex flex-col items-center pt-[24px] border-t border-[#D9D9D9]">
              <div className="text-[22px] text-red-brand font-semibold">
                Poti primi pana la{" "}
                {
                  devices[selection.make][selection.model][selection.memory]
                    .grades[deviceGrade]
                }{" "}
                lei
              </div>

              <div className="text-[14px] text-center mt-[12px]">
                Pentru a stabili valoarea finală, te așteptăm în magazin, unde
                un reprezentant va inspecta și evalua dispozitivul tău.
              </div>

              <div className="mt-[20px] sm:max-w-fit">
                <Button
                  variant="secondary"
                  title="Inchide"
                  display="block"
                  onClick={() => removeModal("buy-back-simulation-modal")}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default connect(null, { removeModal, addAlert })(PhoneBuyBackSimulation);
